import { Component, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    createBlock,
    linkTextField,
    headingField,
    htmlField,
    buttonField,
    arrayField,
    textField,
    descriptiveImageField,
} from "./blockBuilder";

import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import {
    SimpleCentered,
    SplitWithImageLeft,
    SplitWithImageRight,
    SplitWithScreenshot,
    WithAlignedImageLeft,
    WithAlignedImageRight,
    WithAppScreenshot,
    WithOffsetImage,
} from "@/views/blocks/hero/hero";
import type { HeroBaseProps } from "@/views/blocks/hero/hero.interfaces";
import { getStockPhotoUrl, AVATAR_API_BASE_URL } from "@/apps/page/services/page.service";
import { useFieldConfig } from "./useFieldConfig";

export function useHeros() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    function createBaseHeroBlock({
        component,
        typeId,
        name,
        extendBlock,
    }: {
        component: Component;
        typeId: string;
        name: string;
        extendBlock?: (block: ReturnType<typeof createBlock>) => ReturnType<typeof createBlock>;
    }): BlockDef {
        let block = createBlock<Omit<HeroBaseProps, "image">>({
            typeId,
            type: "hero",
            name: name,
            component,
        })
            .withField(
                "socialProofText",
                textField(
                    translations.value.socialProofText,
                    "Join 10,000+ Happy Customers",
                    translations.value.text
                )
            )
            .withArrayField(
                "socialProofImages",
                arrayField(
                    translations.value.image,
                    "socialProofImages",
                    {
                        src: { label: translations.value.src, type: "image" },
                        alt: { label: translations.value.alt, type: "text" },
                    },
                    [
                        { src: `${AVATAR_API_BASE_URL}/f/1`, alt: "Participant 1" },
                        { src: `${AVATAR_API_BASE_URL}/m/1`, alt: "Participant 2" },
                        { src: `${AVATAR_API_BASE_URL}/f/2`, alt: "Participant 3" },
                        { src: `${AVATAR_API_BASE_URL}/m/2`, alt: "Participant 4" },
                        { src: `${AVATAR_API_BASE_URL}/f/3`, alt: "Participant 5" },
                    ],
                    {
                        src: `${AVATAR_API_BASE_URL}/f/4`,
                        alt: "Participant X",
                    }
                )
            )
            .withField(
                "tag",
                linkTextField(
                    translations.value.tag,
                    {
                        text: "New features available",
                        label: "Learn more",
                        href: "#",
                    },
                    {
                        text: translations.value.text,
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "title",
                headingField(translations.value.title, "Build Something Amazing Together", "h1", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "paragraph",
                htmlField(
                    translations.value.paragraph,
                    "Connect with like-minded people, share experiences, and create lasting memories. Join our vibrant community where friendships flourish and great ideas come to life.",
                    translations.value.text
                )
            )
            .withArrayField(
                "details",
                arrayField(
                    translations.value.detail,
                    "details",
                    {
                        title: { label: translations.value.title },
                        description: { label: translations.value.description },
                    },
                    [
                        {
                            title: "50K+",
                            description: "Active Members",
                        },
                        {
                            title: "95%",
                            description: "Satisfaction Rate",
                        },
                        {
                            title: "24/7",
                            description: "Community Support",
                        },
                    ],
                    {
                        title: "New item",
                        description: "New description",
                    }
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Join our community",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: "Learn more",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ]);

        if (extendBlock) {
            block = extendBlock(block);
        }

        return block.build();
    }

    const simpleCenteredBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "SimpleCentered",
            name: t("page.blocks.hero.simpleCentered"),
            component: SimpleCentered,
        })
    );

    const splitWithImageLeftBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            component: SplitWithImageLeft,
            typeId: "SplitWithImageLeft",
            name: t("page.blocks.hero.splitWithImageLeft"),
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: getStockPhotoUrl(
                                "communities",
                                "diverse-friends-gathering-together"
                            ),
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const splitWithImageRightBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            component: SplitWithImageRight,
            typeId: "SplitWithImageRight",
            name: t("page.blocks.hero.splitWithImageRight"),
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: getStockPhotoUrl(
                                "communities",
                                "close-up-happy-people-posing-together"
                            ),
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const splitWithScreenshotBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "SplitWithScreenshot",
            name: t("page.blocks.hero.splitWithScreenshot"),
            component: SplitWithScreenshot,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: getStockPhotoUrl(
                                "licences-saas",
                                "teamwork-programmer-working-with-computer"
                            ),
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withAppScreenshotBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAppScreenshot",
            name: t("page.blocks.hero.withAppScreenshot"),
            component: WithAppScreenshot,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: getStockPhotoUrl(
                                "licences-saas",
                                "cheerful-web-developers-working-laptop"
                            ),
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withAlignedImageLeftBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAlignedImageLeft",
            name: t("page.blocks.hero.withAlignedImageLeft"),
            component: WithAlignedImageLeft,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: getStockPhotoUrl("communities", "friends-stacking-hands"),
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withAlignedImageRightBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAlignedImageRight",
            name: t("page.blocks.hero.withAlignedImageRight"),
            component: WithAlignedImageRight,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: getStockPhotoUrl(
                                "communities",
                                "smiley-friends-outdoors-having-fun-city"
                            ),
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    const withOffsetImageBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithOffsetImage",
            name: t("page.blocks.hero.withOffsetImage"),
            component: WithOffsetImage,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField(
                        translations.value.src,
                        {
                            src: getStockPhotoUrl(
                                "communities",
                                "group-people-hugging-each-other-park"
                            ),
                            alt: "Hero image",
                        },
                        {
                            src: translations.value.src,
                            alt: translations.value.alt,
                        }
                    )
                ),
        })
    );

    return {
        blocks: computed(() => [
            simpleCenteredBlock.value,
            splitWithImageLeftBlock.value,
            splitWithImageRightBlock.value,
            splitWithScreenshotBlock.value,
            withAppScreenshotBlock.value,
            withAlignedImageLeftBlock.value,
            withAlignedImageRightBlock.value,
            withOffsetImageBlock.value,
        ]),
    };
}
